import { ActionContext, ActionTree, GetterTree, MutationTree } from 'vuex'
import { IRootState, ICartState, IOrderResponse } from '@/models/Store.model'
import { EDiscountType } from '@/models/POS.model'

type TAction = ActionTree<ICartState, IRootState>
type TActionContext = ActionContext<ICartState, IRootState>
type TMutation = MutationTree<ICartState>
type TGetter = GetterTree<ICartState, IRootState>

const state: ICartState = {
  raw: null,
  cart: {
    paymentMethod: '',
    discount: 0,
    commonDiscount: 0,
    voucherDiscount: 0,
    totalPrice: 0,
    totalWithDiscount: 0,
    paidByCustomer: 0,
    change: 0,
    customerId: '',
    customerName: '',
    warehouseId: '',
    excludeDiscount: 0,
    isPreOrder: false,
    voucherIds: [],
    discountType: EDiscountType.COMMON
  },
  product: [],
  orderResponse: {
    id: null,
    discount: 0,
    commonDiscount: 0,
    voucherDiscount: 0,
    merchantId: null,
    orderNo: null,
    paidByCustomer: 0,
    paymentDate: new Date(),
    paymentMethod: '',
    products: [],
    totalPrice: 0,
    excludeDiscount: 0,
    warehouseId: 0
  }
}

const actions: TAction = {
  setRaw ({ commit }: TActionContext, payload: any): void {
    commit('SET_RAW', payload)
  },
  setCart ({ commit }: TActionContext, payload: any): void {
    commit('SET_CART', payload)
  },
  setSummary ({ commit }: TActionContext, payload: any): void {
    commit('SET_SUMMARY', payload)
  },
  setExcludeDiscount ({ commit }: TActionContext, discount: number): void {
    commit('SET_EXCLUDE_DISCOUNT', discount)
  },
  setDiscount ({ commit }: TActionContext, discount: number): void {
    commit('SET_DISCOUNT', discount)
  },
  setCommonDiscount ({ commit }: TActionContext, discount: number): void {
    commit('SET_COMMON_DISCOUNT', discount)
  },
  setVoucherDiscount ({ commit }: TActionContext, voucherDiscount: number): void {
    commit('SET_VOUCHER_DISCOUNT', voucherDiscount)
  },
  setOrderResponse ({ commit }: TActionContext, payload: IOrderResponse): void {
    commit('SET_RESPONSE_ORDER', payload)
  },
  calTotalWithDiscount ({ commit }: TActionContext): void {
    commit('CAL_TOTAL_DISCOUNT')
  },
  clearRaw ({ commit }: TActionContext): void {
    commit('SET_RAW', null)
  },
  setVoucherIds ({ commit }: TActionContext, voucherIds: number[]): void {
    commit('SET_VOUCHER_IDS', voucherIds)
  },
  setDiscountType ({ commit }: TActionContext, discountType: EDiscountType): void {
    commit('SET_DISCOUNT_TYPE', discountType)
  }
}

const mutations: TMutation = {
  SET_RAW (state: ICartState, payload: any): void {
    state.raw = payload
  },
  SET_CART (state: ICartState, payload: any): void {
    state.product = payload
  },
  SET_SUMMARY (state: ICartState, payload: any): void {
    state.cart = {
      discount: payload.discount,
      totalPrice: payload.totalPrice,
      paymentMethod: '',
      paidByCustomer: payload.paidByCustomer || 0,
      totalWithDiscount: payload.totalWIthDiscount || 0,
      change: payload.change || 0,
      customerId: payload.customerId,
      customerName: payload.customerName,
      warehouseId: '',
      isPreOrder: payload?.isPreOrder,
      voucherIds: state.cart.voucherIds,
      voucherDiscount: payload.voucherDiscount,
      discountType: state.cart.discountType
    }
  },
  SET_EXCLUDE_DISCOUNT (state: ICartState, discount: number): void {
    state.cart.excludeDiscount = discount
  },
  SET_COMMON_DISCOUNT (state: ICartState, discount: number): void {
    state.cart.commonDiscount = discount
  },
  SET_DISCOUNT (state: ICartState, discount: number): void {
    state.cart.discount = discount
  },
  SET_VOUCHER_DISCOUNT (state: ICartState, voucherDiscount: number): void {
    state.cart.voucherDiscount = voucherDiscount
  },
  SET_RESPONSE_ORDER (state: ICartState, payload: any): void {
    state.orderResponse = payload
  },
  CAL_TOTAL_DISCOUNT (state: ICartState): void {
    const total = state.cart.totalPrice
    - ((state.cart.discount || 0) + (state.cart.commonDiscount || 0) + (state.cart.voucherDiscount || 0))
    state.cart.totalWithDiscount = Math.max(total, 0)
  },
  SET_VOUCHER_IDS (state: ICartState, voucherIds: number[]): void {
    state.cart.voucherIds = voucherIds
  },
  SET_DISCOUNT_TYPE (state: ICartState, discountType: EDiscountType): void {
    state.cart.discountType = discountType
  }
}

const getters: TGetter = {
  getRaw: (state: ICartState): any => state.raw,
  getCart: (state: ICartState): any => state.product,
  getSummary: (state: ICartState): any => state.cart,
  getOrderResponse: (state: ICartState): any => state.orderResponse,
  getVoucherIds: (state: ICartState): any => state.cart,
  getDiscountType: (state: ICartState): any => state.cart.discountType
}

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
}
